import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon } from "./index.jsx"
import { Btn, Progress } from "./components"

/* ============================================================
   Unggah berkas — satu pola untuk semua tempat yang menerima dokumen.

   Alurnya selalu sama: pilih/seret berkas → lihat nama dan ukurannya →
   tekan Unggah → pantau kemajuannya (bisa dibatalkan). Tidak ada yang
   terunggah hanya karena berkasnya dipilih.

   FileDropzone    area seret-dan-lepas + validasi tipe/ukuran
   UploadProgress  bilah kemajuan sungguhan (byte terkirim) + Batal
   useUpload       kemajuan & pembatalan untuk satu unggahan
   DocUploader     gabungan ketiganya, untuk unggahan langsung
   ============================================================ */

const tr = (lang, id, en) => (lang === "id" ? id : en)

export function fmtBytes(b) {
  if (b == null) return ""
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`
  return `${Number((b / 1024 / 1024).toFixed(1))} MB`
}

function extOf(name) {
  const i = String(name).lastIndexOf(".")
  return i < 0 ? "" : String(name).slice(i + 1).toLowerCase()
}

const keyOf = (f) => `${f.name}:${f.size}:${f.lastModified}`

export function isCanceled(ex) {
  return ex?.code === "ERR_CANCELED" || ex?.name === "CanceledError"
}

// Pemeriksaan di sisi klien supaya kesalahan tipe/ukuran ketahuan sebelum
// berkasnya ikut terkirim (dan menghabiskan waktu di jaringan yang lambat).
// Server tetap memeriksa ulang; ini hanya soal umpan balik yang cepat.
function validateFiles(files, { accept, maxBytes, lang }) {
  const valid = []
  const errors = []
  for (const f of files) {
    if (accept?.length && !accept.includes(extOf(f.name))) {
      errors.push(tr(lang,
        `"${f.name}": tipe tidak didukung. Gunakan ${accept.map((e) => e.toUpperCase()).join(", ")}.`,
        `"${f.name}": unsupported type. Use ${accept.map((e) => e.toUpperCase()).join(", ")}.`))
    } else if (f.size === 0) {
      errors.push(tr(lang, `"${f.name}": berkas kosong.`, `"${f.name}": the file is empty.`))
    } else if (maxBytes && f.size > maxBytes) {
      errors.push(tr(lang,
        `"${f.name}" berukuran ${fmtBytes(f.size)}, melebihi batas ${fmtBytes(maxBytes)}.`,
        `"${f.name}" is ${fmtBytes(f.size)}, over the ${fmtBytes(maxBytes)} limit.`))
    } else {
      valid.push(f)
    }
  }
  return { valid, errors }
}

// ── Area pilih/seret berkas ───────────────────────────────────
export function FileDropzone({ accept, maxBytes, multiple = false, disabled = false, compact = false, onFiles }) {
  const { lang } = useApp()
  const inputRef = React.useRef(null)
  const [over, setOver] = React.useState(false)
  const [error, setError] = React.useState(null)

  function handle(list) {
    const files = Array.from(list || [])
    if (files.length === 0) return
    if (!multiple && files.length > 1) {
      setError(tr(lang, "Pilih satu berkas saja.", "Choose a single file."))
      return
    }
    const { valid, errors } = validateFiles(files, { accept, maxBytes, lang })
    setError(errors.length ? errors.join(" ") : null)
    if (valid.length) onFiles(valid)
  }

  const formats = (accept || []).map((e) => e.toUpperCase()).join(", ")
  const limit = maxBytes ? ` · ${tr(lang, "maks.", "max")} ${Math.round(maxBytes / 1024 / 1024)} MB` : ""
  const open = () => { if (!disabled) inputRef.current?.click() }

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={open}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open() } }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true) }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false) }}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) handle(e.dataTransfer.files) }}
        style={{
          border: `2px dashed ${over ? "var(--program-color)" : "var(--border-strong)"}`,
          background: over ? "var(--program-color-soft)" : "var(--surface-2)",
          borderRadius: 10,
          padding: compact ? "10px 14px" : "18px 16px",
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "background .15s, border-color .15s",
        }}
      >
        <div className="row gap-2" style={{ justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
          <Icon name="upload" size={compact ? 14 : 18} color="var(--text-3)" />
          <span className="small" style={{ fontWeight: 500 }}>
            {multiple ? tr(lang, "Tarik berkas ke sini atau ", "Drag files here or ") : tr(lang, "Tarik berkas ke sini atau ", "Drag a file here or ")}
            <span style={{ color: "var(--program-color-text)", textDecoration: "underline" }}>{tr(lang, "pilih dari komputer", "browse")}</span>
          </span>
        </div>
        <div className="tiny muted" style={{ marginTop: compact ? 2 : 4 }}>{formats}{limit}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={(accept || []).map((e) => `.${e}`).join(",")}
        onChange={(e) => { handle(e.target.files); e.target.value = "" }}
      />
      {error && <div role="alert" className="tiny" style={{ color: "var(--danger)", marginTop: 6 }}>{error}</div>}
    </div>
  )
}

// ── Bilah kemajuan ────────────────────────────────────────────
// Persentasenya byte yang benar-benar sudah terkirim. Begitu semuanya terkirim,
// server masih menyimpan berkasnya — itu ditampilkan sebagai "memproses", bukan
// 100% yang seolah sudah selesai.
export function UploadProgress({ name, loaded = 0, total = 0, position, onCancel }) {
  const { lang } = useApp()
  const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0
  const processing = total > 0 && loaded >= total
  return (
    <div role="status" aria-live="polite" style={{ padding: "8px 10px", background: "var(--surface-2)", borderRadius: 6 }}>
      <div className="row gap-2" style={{ alignItems: "center" }}>
        <Icon name="doc" size={13} color="var(--text-3)" />
        <span className="small" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        {position && position.n > 1 && (
          <span className="tiny muted">{position.i} {tr(lang, "dari", "of")} {position.n}</span>
        )}
        {onCancel && <Btn type="button" variant="ghost" size="sm" onClick={onCancel}>{tr(lang, "Batal", "Cancel")}</Btn>}
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={processing ? undefined : pct}
        aria-label={tr(lang, "Kemajuan unggahan", "Upload progress")}
        style={{ marginTop: 6 }}
      >
        <Progress value={processing ? 100 : pct} />
      </div>
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {processing
          ? tr(lang, "Memproses di server…", "Processing on the server…")
          : `${fmtBytes(loaded)} ${tr(lang, "dari", "of")} ${fmtBytes(total)} · ${pct}%`}
      </div>
    </div>
  )
}

// ── Kemajuan & pembatalan satu unggahan ───────────────────────
// run(file, send): send menerima { onProgress, signal } untuk diteruskan ke
// fungsi API. Pembatalan melempar galat yang dikenali isCanceled().
export function useUpload() {
  const [progress, setProgress] = React.useState(null) // { name, loaded, total } | null
  const ctrl = React.useRef(null)

  // Unggahan yang masih berjalan tidak boleh terus mengirim setelah layarnya hilang.
  React.useEffect(() => () => ctrl.current?.abort(), [])

  const run = React.useCallback(async (file, send) => {
    const c = new AbortController()
    ctrl.current = c
    setProgress({ name: file.name, loaded: 0, total: file.size })
    try {
      return await send({
        signal: c.signal,
        onProgress: (loaded, total) =>
          setProgress((p) => (p ? { ...p, loaded, total: total || p.total } : p)),
      })
    } finally {
      setProgress(null)
      ctrl.current = null
    }
  }, [])

  const cancel = React.useCallback(() => ctrl.current?.abort(), [])
  return { progress, busy: progress !== null, run, cancel }
}

// ── Unggahan langsung: pilih → pratinjau → Unggah → kemajuan ──
// `successMessage(jumlah)` mengganti kalimat "Dokumen diunggah" untuk unggahan yang
// bukan dokumen (mis. gambar). `upload(file, { onProgress, signal })` mengirim satu berkas. Dengan `multiple`,
// berkas diunggah berurutan; yang gagal atau belum sempat terkirim tetap berada
// di daftar supaya bisa dicoba lagi tanpa memilih ulang.
export function DocUploader({ accept, maxBytes, multiple = false, upload, onUploaded, successMessage }) {
  const { lang } = useApp()
  const [pending, setPending] = React.useState([])
  const [errors, setErrors] = React.useState([])
  const [running, setRunning] = React.useState(false)
  const [position, setPosition] = React.useState({ i: 0, n: 0 })
  const up = useUpload()

  function add(files) {
    setErrors([])
    setPending((prev) => {
      const base = multiple ? prev : []
      const seen = new Set(base.map(keyOf))
      return [...base, ...files.filter((f) => !seen.has(keyOf(f)))]
    })
  }

  async function start() {
    const queue = pending
    const done = new Set()
    const failed = []
    let cancelled = false
    setErrors([])
    setRunning(true)
    for (let i = 0; i < queue.length; i++) {
      const f = queue[i]
      setPosition({ i: i + 1, n: queue.length })
      try {
        await up.run(f, (o) => upload(f, o))
        done.add(f)
      } catch (ex) {
        if (isCanceled(ex)) { cancelled = true; break }
        failed.push(`${f.name}: ${ex?.response?.data?.error || ex.message}`)
      }
    }
    setRunning(false)
    setPending(queue.filter((f) => !done.has(f)))
    setErrors(cancelled ? [...failed, tr(lang, "Unggahan dibatalkan.", "Upload cancelled.")] : failed)
    if (done.size) {
      toast.success(successMessage
        ? successMessage(done.size)
        : done.size > 1
          ? tr(lang, `${done.size} dokumen diunggah`, `${done.size} documents uploaded`)
          : tr(lang, "Dokumen diunggah", "Document uploaded"))
      onUploaded?.()
    }
  }

  const showDropzone = !running && (multiple || pending.length === 0)
  const current = up.progress || (running && pending[position.i - 1]
    ? { name: pending[position.i - 1].name, loaded: 0, total: pending[position.i - 1].size }
    : null)

  return (
    <div className="col gap-2">
      {running && current && (
        <UploadProgress
          name={current.name}
          loaded={current.loaded}
          total={current.total}
          position={position}
          onCancel={up.cancel}
        />
      )}

      {!running && pending.length > 0 && (
        <div className="col gap-1">
          {pending.map((f) => (
            <div key={keyOf(f)} className="row gap-2" style={{ alignItems: "center", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
              <Icon name="doc" size={13} color="var(--text-3)" />
              <span className="small" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span className="tiny muted">{fmtBytes(f.size)}</span>
              <Btn
                type="button"
                variant="ghost"
                size="sm"
                aria-label={tr(lang, `Lepas ${f.name}`, `Remove ${f.name}`)}
                icon={<Icon name="close" size={12} />}
                onClick={() => setPending((prev) => prev.filter((x) => x !== f))}
              />
            </div>
          ))}
          <div className="row gap-2">
            <Btn type="button" variant="primary" size="sm" icon={<Icon name="upload" size={12} />} onClick={start}>
              {pending.length > 1 ? tr(lang, `Unggah ${pending.length} berkas`, `Upload ${pending.length} files`) : tr(lang, "Unggah", "Upload")}
            </Btn>
            <Btn type="button" variant="ghost" size="sm" onClick={() => { setPending([]); setErrors([]) }}>{tr(lang, "Batal", "Cancel")}</Btn>
          </div>
        </div>
      )}

      {showDropzone && (
        <FileDropzone accept={accept} maxBytes={maxBytes} multiple={multiple} compact={pending.length > 0} onFiles={add} />
      )}

      {errors.length > 0 && (
        <div role="alert" className="col gap-1">
          {errors.map((m, i) => (
            <div key={i} className="tiny" style={{ color: "var(--danger)" }}>{m}</div>
          ))}
        </div>
      )}
    </div>
  )
}
